import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

/**
 * Public coupon-submission flow. Auth-gated (sanctum required by the API).
 * Posts to POST /v1/coupons/submit; the resulting coupon lands as
 * source=user_submitted, submission_status=pending, is_active=false until
 * an admin approves it via the moderation queue.
 */
export default function SubmitCouponPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    merchant_name:  '',
    merchant_url:   '',
    title:          '',
    description:    '',
    terms:          '',
    discount_type:  'percent',
    discount_value: '',
    code:           '',
    expires_at:     '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(null); // { id, title, … }
  const [errors, setErrors]         = useState({});
  const [generalError, setGeneralError] = useState('');

  if (!user) {
    // Bounce to login, preserve the intent so the user lands here after auth.
    return <Navigate to="/login" state={{ from: '/submit-coupon' }} replace />;
  }

  const f = (k) => ({
    value: form[k],
    onChange: (e) => setForm(p => ({ ...p, [k]: e.target.value })),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({}); setGeneralError(''); setSubmitting(true);

    // Strip empty optional fields so backend's nullable rules pass cleanly.
    const payload = Object.fromEntries(
      Object.entries(form).filter(([_, v]) => v !== '' && v != null)
    );
    payload.discount_value = Number(payload.discount_value);

    try {
      const { data } = await api.post('/coupons/submit', payload);
      setSubmitted(data.data);
    } catch (err) {
      const fields = err.response?.data?.errors;
      if (fields) {
        // Flatten Laravel's {field: [msg]} shape into {field: msg}
        setErrors(Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, v[0]])));
      } else {
        setGeneralError(err.response?.data?.error || err.response?.data?.message || 'Could not submit. Try again.');
      }
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div>
        <section style={{ background: 'var(--hero-gradient)', color: '#fff', padding: '72px 0 48px', textAlign: 'center' }}>
          <div className="container-narrow">
            <h1 style={{ fontSize: 'clamp(28px, 3.6vw, 42px)', fontWeight: 800, letterSpacing: '-0.025em' }}>Thanks for the tip! 🙌</h1>
            <p style={{ marginTop: 14, opacity: 0.9, fontSize: 16 }}>Your submission is in review.</p>
          </div>
        </section>
        <div className="container-narrow" style={{ padding: '56px 20px' }}>
          <div className="alert alert-success" style={{ fontSize: 15 }}>
            <strong>Submitted:</strong> "{submitted.title}". We typically review within 24 hours.
            Approved coupons go live on the catalog and you'll be able to see them under your wallet.
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 24, flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary"
              onClick={() => { setSubmitted(null); setForm({ merchant_name: '', merchant_url: '', title: '', description: '', terms: '', discount_type: 'percent', discount_value: '', code: '', expires_at: '' }); }}
            >Submit another</button>
            <Link to="/coupons" className="btn btn-primary">Browse coupons</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section style={{ background: 'var(--hero-gradient)', color: '#fff', padding: '64px 0 48px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-30%', right: '-10%', width: 420, height: 420, borderRadius: '50%', background: 'radial-gradient(circle, rgba(217,70,239,0.3) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="container-narrow" style={{ position: 'relative', textAlign: 'center' }}>
          <span className="chip" style={{ marginBottom: 18 }}>✉️ Help the community</span>
          <h1 style={{ fontSize: 'clamp(30px, 4vw, 46px)', fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1, marginBottom: 14 }}>
            Submit a <span className="gradient-text">coupon</span>
          </h1>
          <p style={{ fontSize: 16, opacity: 0.85, maxWidth: 540, margin: '0 auto', lineHeight: 1.5 }}>
            Found a working code we don't have? Share it. We review every submission within 24 hours.
          </p>
        </div>
      </section>

      <div className="container-narrow" style={{ padding: '48px 20px 72px' }}>
        {generalError && <div className="alert alert-danger" style={{ marginBottom: 20 }}>{generalError}</div>}

        <form onSubmit={handleSubmit} style={{ background: '#fff', border: '1px solid #E4E7F0', borderRadius: 16, padding: 28 }}>
          {/* Merchant block */}
          <h3 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8B94A8', marginBottom: 14 }}>Merchant</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }} className="submit-grid">
            <Field label="Merchant name *" error={errors.merchant_name}>
              <input className="form-input" required placeholder="e.g. Nike" {...f('merchant_name')} />
            </Field>
            <Field label="Merchant URL" error={errors.merchant_url} hint="Optional, helps us match existing brands">
              <input className="form-input" type="url" placeholder="https://nike.com" {...f('merchant_url')} />
            </Field>
          </div>

          {/* Coupon block */}
          <h3 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8B94A8', marginBottom: 14 }}>Coupon</h3>
          <Field label="Title *" error={errors.title}>
            <input className="form-input" required maxLength={200} placeholder="e.g. 20% off your first order" {...f('title')} />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }} className="submit-grid">
            <Field label="Discount type *" error={errors.discount_type}>
              <select className="form-input form-select" {...f('discount_type')}>
                <option value="percent">% off</option>
                <option value="fixed">$ off</option>
                <option value="freebie">Free gift</option>
                <option value="bogo">BOGO</option>
                <option value="bundle">Bundle</option>
              </select>
            </Field>
            <Field label="Value *" error={errors.discount_value}>
              <input className="form-input" type="number" step="0.01" min="0" required placeholder="20" {...f('discount_value')} />
            </Field>
            <Field label="Code" error={errors.code} hint="Leave blank if it's a no-code deal">
              <input className="form-input" placeholder="SAVE20" {...f('code')}
                style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }} />
            </Field>
          </div>

          <Field label="Description" error={errors.description} hint="Optional context — what does the deal include?">
            <textarea className="form-input" rows={2} maxLength={2000} {...f('description')} />
          </Field>

          <Field label="Terms / restrictions" error={errors.terms} hint="Min order, exclusions, expiry detail">
            <textarea className="form-input" rows={2} maxLength={2000} {...f('terms')} />
          </Field>

          <Field label="Expires on" error={errors.expires_at} hint="Optional. When does the deal end?">
            <input className="form-input" type="date" {...f('expires_at')} />
          </Field>

          {/* Trust note */}
          <div style={{ background: '#F7F8FB', borderRadius: 10, padding: '14px 16px', marginTop: 8, marginBottom: 20, fontSize: 13, color: '#4A5568', lineHeight: 1.5 }}>
            <strong>One note:</strong> Please only submit codes you've actually used or seen work.
            Submitting fake codes may get your account suspended. We allow up to 3 submissions per merchant per day.
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Link to="/coupons" className="btn btn-secondary">Cancel</Link>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit coupon'}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        @media (max-width: 720px) {
          .submit-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

function Field({ label, hint, error, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
      {error && <p style={{ color: '#E53E3E', fontSize: 12, marginTop: 6 }}>{error}</p>}
      {!error && hint && <p style={{ color: '#8B94A8', fontSize: 12, marginTop: 6 }}>{hint}</p>}
    </div>
  );
}
